// src/pages/Clientes.jsx
import React, { useState, useEffect } from "react";
import api from "../services/api";
import Swal from "sweetalert2";
import { useFiscal } from "../hooks/useFiscal";

const Clientes = () => {
  const fiscal = useFiscal();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [busqueda, setBusqueda] = useState("");

  const [form, setForm] = useState({
    nombre: "",
    rnc_cedula: "",
    telefono: "",
    email: "",
    direccion: "",
    tipo: "general",
  });

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      setLoading(true);
      const res = await api.get("/clientes?activos=true");
      setClientes(res.data);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los clientes",
      });
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (cliente = null) => {
    if (cliente) {
      setClienteEditando(cliente);
      setForm({
        nombre: cliente.nombre,
        rnc_cedula: cliente.rnc_cedula || "",
        telefono: cliente.telefono || "",
        email: cliente.email || "",
        direccion: cliente.direccion || "",
        tipo: cliente.tipo,
      });
    } else {
      setClienteEditando(null);
      setForm({
        nombre: "",
        rnc_cedula: "",
        telefono: "",
        email: "",
        direccion: "",
        tipo: "general",
      });
    }
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setClienteEditando(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nombre.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "El nombre es obligatorio",
      });
      return;
    }

    try {
      if (clienteEditando) {
        await api.put(`/clientes/${clienteEditando.id}`, form);
        Swal.fire({
          icon: "success",
          title: "Cliente actualizado",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await api.post("/clientes", form);
        Swal.fire({
          icon: "success",
          title: "Cliente creado",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      cerrarModal();
      cargarClientes();
    } catch (error) {
      console.error("Error al guardar cliente:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || "No se pudo guardar el cliente",
      });
    }
  };

  const desactivarCliente = async (id) => {
    const result = await Swal.fire({
      icon: "question",
      title: "¿Desactivar cliente?",
      text: "El cliente no se eliminará, solo se marcará como inactivo",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/clientes/${id}`);
      Swal.fire({
        icon: "success",
        title: "Cliente desactivado",
        timer: 1500,
        showConfirmButton: false,
      });
      cargarClientes();
    } catch (error) {
      console.error("Error al desactivar cliente:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo desactivar el cliente",
      });
    }
  };

  const clientesFiltrados = clientes.filter((c) =>
    c.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (c.codigo && c.codigo.toLowerCase().includes(busqueda.toLowerCase())) ||
    (c.rnc_cedula && c.rnc_cedula.toLowerCase().includes(busqueda.toLowerCase()))
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 Clientes</h1>
          <p style={styles.subtitle}>Gestión de clientes</p>
        </div>
        <button onClick={() => abrirModal()} style={styles.btnNuevo}>
          ➕ Nuevo Cliente
        </button>
      </div>

      {/* Buscador */}
      <div style={styles.searchBox}>
        <input
          type="text"
          placeholder="🔍 Buscar por nombre, código o RNC..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Tabla */}
      {loading ? (
        <div style={styles.loading}>Cargando clientes...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Código</th>
                <th style={styles.th}>Nombre</th>
                <th style={styles.th}>{fiscal.id_fiscal_empresa_label}/{fiscal.id_fiscal_persona_label}</th>
                <th style={styles.th}>Teléfono</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {clientesFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="7" style={styles.emptyCell}>
                    No hay clientes registrados
                  </td>
                </tr>
              ) : (
                clientesFiltrados.map((cliente) => (
                  <tr key={cliente.id} style={styles.tableRow}>
                    <td style={styles.td}>{cliente.codigo}</td>
                    <td style={styles.td}>
                      <strong>{cliente.nombre}</strong>
                    </td>
                    <td style={styles.td}>{cliente.rnc_cedula || "-"}</td>
                    <td style={styles.td}>{cliente.telefono || "-"}</td>
                    <td style={styles.td}>{cliente.email || "-"}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          background:
                            cliente.tipo === "frecuente" ? "#dbeafe" : "#f1f5f9",
                          color:
                            cliente.tipo === "frecuente" ? "#1e40af" : "#475569",
                        }}
                      >
                        {cliente.tipo}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          onClick={() => abrirModal(cliente)}
                          style={styles.btnEditar}
                        >
                          ✏️
                        </button>
                        {cliente.codigo !== "CLI-000" && (
                          <button
                            onClick={() => desactivarCliente(cliente.id)}
                            style={styles.btnEliminar}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalAbierto && (
        <div style={styles.modalOverlay} onClick={cerrarModal}>
          <div
            style={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {clienteEditando ? "Editar Cliente" : "Nuevo Cliente"}
              </h2>
              <button onClick={cerrarModal} style={styles.btnCerrar}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nombre *</label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{fiscal.id_fiscal_empresa_label}/{fiscal.id_fiscal_persona_label}</label>
                  <input
                    type="text"
                    value={form.rnc_cedula}
                    onChange={(e) =>
                      setForm({ ...form, rnc_cedula: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Teléfono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) =>
                      setForm({ ...form, telefono: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Dirección</label>
                <textarea
                  value={form.direccion}
                  onChange={(e) =>
                    setForm({ ...form, direccion: e.target.value })
                  }
                  style={{ ...styles.input, minHeight: "80px", resize: "vertical" }}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Tipo</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  style={styles.input}
                >
                  <option value="general">General</option>
                  <option value="frecuente">Frecuente</option>
                  <option value="corporativo">Corporativo</option>
                </select>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={cerrarModal}
                  style={styles.btnCancelar}
                >
                  Cancelar
                </button>
                <button type="submit" style={styles.btnGuardar}>
                  💾 Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    background: "#f8fafc",
    padding: "20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: "5px 0 0 0",
  },
  btnNuevo: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
  },
  searchBox: {
    marginBottom: "20px",
  },
  searchInput: {
    width: "100%",
    maxWidth: "500px",
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box",
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
  },
  tableContainer: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f8fafc",
  },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    borderBottom: "2px solid #e2e8f0",
  },
  tableRow: {
    borderBottom: "1px solid #f1f5f9",
  },
  td: {
    padding: "12px 16px",
    fontSize: "14px",
    color: "#1e293b",
  },
  emptyCell: {
    padding: "40px",
    textAlign: "center",
    color: "#94a3b8",
  },
  badge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
  },
  actions: {
    display: "flex",
    gap: "8px",
  },
  btnEditar: {
    padding: "6px 12px",
    background: "#f1f5f9",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  btnEliminar: {
    padding: "6px 12px",
    background: "#fee2e2",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    background: "white",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "600px",
    maxHeight: "90vh",
    overflow: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderBottom: "1px solid #e2e8f0",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
  },
  btnCerrar: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#64748b",
  },
  form: {
    padding: "20px",
  },
  formGroup: {
    marginBottom: "16px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e2e8f0",
  },
  btnCancelar: {
    padding: "10px 20px",
    background: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnGuardar: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

export default Clientes;